import { ref, computed, onMounted, reactive, watch } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useRoute } from '@/composables/common/router/useRouter'

export function useCategoriaForm(getInitialData) {
    const route = useRoute()

    const indumentarias = ref([])
    const representantes = ref([])
    const frecuencias = ref([])
    const frecuenciasLoading = ref(false)
    const loadingIndumentarias = ref(false)
    const loadingRepresentantes = ref(false)

    const listaId = computed(() => route.params.lista_id || store.getters.currentListaId)

    function toFormData(data) {
        return {
            name: data.name || '',
            precio: data.precio || null,
            indumentarias_ids: data.indumentariasSelect
                ? data.indumentariasSelect.map(i => i.id)
                : (Array.isArray(data.indumentarias_ids) ? [...data.indumentarias_ids] : []),
            observacion_indumentarias: data.observacion_indumentarias || '',
            activar_pago_plazos: !!data.activar_pago_plazos,
            frecuencia_ids: data.frecuencias
                ? data.frecuencias.map(f => f.id)
                : (Array.isArray(data.frecuencia_ids) ? [...data.frecuencia_ids] : []),
            min_cantidad_plazos: data.min_cantidad_plazos || null,
            max_cantidad_plazos: data.max_cantidad_plazos || null,
            representante_id: data.representante?.id || data.representante_id || null,
            taquilla: !!data.taquilla,
            online: !!data.online
        }
    }

    const formData = reactive(toFormData(getInitialData() || {}))

    const isFormValid = computed(() => {
        return formData.name?.trim() && formData.precio != null && formData.precio !== ''
    })

    // Watch for initialData changes (e.g., when edit modal opens)
    watch(getInitialData, newData => {
        const mapped = toFormData(newData || {})
        Object.keys(mapped).forEach(key => {
            formData[key] = mapped[key]
        })
    }, { deep: true })

    const gd_online = computed(() => store.getters.getGd?.venta_bases_online)
    const gd_taquilla = computed(() => store.getters.getGd?.venta_bases_fisica)

    const precioPlazosData = computed({
        get: () => ({
            activar_pago_plazos: formData.activar_pago_plazos,
            frecuencia_ids: formData.frecuencia_ids,
            min_cantidad_plazos: formData.min_cantidad_plazos,
            max_cantidad_plazos: formData.max_cantidad_plazos
        }),
        set: val => {
            formData.activar_pago_plazos = val.activar_pago_plazos
            formData.frecuencia_ids = val.frecuencia_ids
            formData.min_cantidad_plazos = val.min_cantidad_plazos
            formData.max_cantidad_plazos = val.max_cantidad_plazos
        }
    })

    function handleSubmit(emit) {
        const data = { ...formData }
        emit('submit', data)
    }

    async function getIndumentarias() {
        loadingIndumentarias.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.get-indumentarias'),
                { lista_id: listaId.value, filters: {} }
            )
            indumentarias.value = res.data.indumentarias || []
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching indumentarias:', e)
        } finally {
            loadingIndumentarias.value = false
        }
    }

    async function getFrecuencias() {
        frecuenciasLoading.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.frecuencias.index'),
                { lista_id: listaId.value, filters: { to_select: true } }
            )
            frecuencias.value = res.data.data || []
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching frecuencias:', e)
        } finally {
            frecuenciasLoading.value = false
        }
    }

    async function getRepresentantes() {
        loadingRepresentantes.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.get.representantes'),
                { lista_id: listaId.value, filters: {} }
            )
            representantes.value = res.data.representantes || []
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching representantes:', e)
        } finally {
            loadingRepresentantes.value = false
        }
    }

    onMounted(() => {
        getIndumentarias()
        getRepresentantes()
        getFrecuencias()
    })

    return {
        formData,
        isFormValid,
        indumentarias,
        representantes,
        frecuencias,
        frecuenciasLoading,
        loadingIndumentarias,
        loadingRepresentantes,
        gd_online,
        gd_taquilla,
        precioPlazosData,
        handleSubmit
    }
}
